import {
  ArrowLeftIcon,
  BriefcaseIcon,
  ChevronRightIcon,
  DocumentIcon,
  GraphIcon,
  IdFrontIcon,
  MergeIcon,
  UsersCheckIcon,
} from '@persona-id/design-tokens/icons';
import { last, startCase } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  BASIC_TYPE_OPTIONS,
  PERSONA_OBJECT_TYPE_OPTIONS,
} from 'dashboard/components/PlatformSchemaEditor/constants';
import { isJsonSchemaType } from 'dashboard/components/PlatformSchemaEditor/utils';
import {
  isWorkflowFieldObjectKey,
  REPORT_OBJECT_KEYS,
  VERIFICATION_OBJECT_KEYS,
} from 'dashboard/lib/workflows/objects';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import Select, { Option } from 'design-system/components/Select';
import { JsonSchemaTypes } from 'lib/json-schema';

enum Categories {
  Initial = 'initial',
  Persona = 'persona',
  Report = 'report',
  Verification = 'verification',
  Templates = 'templates',
}

type MenuItemType = JsonSchemaTypes | WorkflowFieldObjectKey | Categories | string;

const BACK_NAV_OPTION = 'back';
const ALL_TEMPLATES_OPTION = 'all_templates';

const SectionedOption = styled(Option)<{
  withSeparator: boolean;
}>`
  flex-grow: 1;
  ${({ withSeparator, theme }) =>
    withSeparator &&
    `
    border-bottom: 1px solid ${theme.color.border.secondary};
    border-radius: 0;
  `}
`;

const PERSONA_OBJECT_ICONS: Record<string, JSX.Element> = {
  account: <UsersCheckIcon />,
  case: <BriefcaseIcon />,
  inquiry: <IdFrontIcon />,
  report: <DocumentIcon />,
  transaction: <MergeIcon />,
  'graph-query': <GraphIcon />,
};

interface TypeSelectProps {
  value: string;
  template: string | null;
  label?: string;
  templateOptionsByObjectKey: Partial<
    Record<WorkflowFieldObjectKey, { label: string; value: string }[]>
  >;
  includePersonaObject: boolean;
  allowNesting: boolean;
  allowArray: boolean;
  onChange: (value: JsonSchemaTypes | WorkflowFieldObjectKey, template?: string) => void;
  setOutsideClickable: (val: boolean) => void;
}

export const TypeSelect: FC<TypeSelectProps> = ({
  value,
  template,
  label,
  templateOptionsByObjectKey,
  includePersonaObject,
  allowNesting,
  allowArray,
  onChange,
  setOutsideClickable,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<MenuItemType>(value);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(template);

  const categoryToOptions = useMemo<Record<Categories, MenuItemType[]>>(() => {
    return {
      [Categories.Initial]: [
        ...(includePersonaObject ? [Categories.Persona] : []),
        ...BASIC_TYPE_OPTIONS,
        ...(allowNesting
          ? allowArray
            ? [JsonSchemaTypes.Array, JsonSchemaTypes.Object]
            : [JsonSchemaTypes.Object]
          : []),
      ],
      [Categories.Persona]: [
        ...Object.values(PERSONA_OBJECT_TYPE_OPTIONS).filter((value) => {
          return !REPORT_OBJECT_KEYS.includes(value) && !VERIFICATION_OBJECT_KEYS.includes(value);
        }),
        Categories.Report,
        Categories.Verification,
      ],
      [Categories.Report]: Object.values(PERSONA_OBJECT_TYPE_OPTIONS).filter((value) =>
        REPORT_OBJECT_KEYS.includes(value),
      ),
      [Categories.Verification]: Object.values(PERSONA_OBJECT_TYPE_OPTIONS).filter((value) =>
        VERIFICATION_OBJECT_KEYS.includes(value),
      ),
      [Categories.Templates]: [], // placeholder, actually filled in dynamically once a WorkflowFieldObjectKey is selected
    };
  }, [allowArray, allowNesting, includePersonaObject]);

  const findCategoryForValue = useCallback(() => {
    if (selectedTemplate) {
      return Categories.Templates;
    }
    const isCategoryKey = (key: string): key is Categories => {
      return Object.values<string>(Categories).includes(key);
    };

    const matchingCategory = Object.entries(categoryToOptions).find(([_, options]) =>
      options.includes(selectedValue),
    )?.[0];

    return matchingCategory && isCategoryKey(matchingCategory)
      ? matchingCategory
      : Categories.Initial;
  }, [selectedValue, selectedTemplate, categoryToOptions]);

  const [currentCategory, setCurrentCategory] = useState<Categories>(findCategoryForValue());

  const allTemplateOptions = useMemo(
    () =>
      Object.values(templateOptionsByObjectKey)
        .flat()
        .map((t) => t.value),
    [templateOptionsByObjectKey],
  );

  const isTerminal = useCallback(
    (type: string) => {
      return (
        type === ALL_TEMPLATES_OPTION ||
        isJsonSchemaType(type) ||
        (isWorkflowFieldObjectKey(type) && !templateOptionsByObjectKey[type]) ||
        allTemplateOptions.includes(type)
      );
    },
    [allTemplateOptions, templateOptionsByObjectKey],
  );

  const typeToOptions = useCallback(
    (types: MenuItemType[], withBackOption: boolean, labels: Record<MenuItemType, string>) => {
      return [
        ...(withBackOption
          ? [
              {
                label: 'Back',
                value: BACK_NAV_OPTION,
                forward: false,
                prefixIcon: <ArrowLeftIcon />,
                withSeparator: false,
                subtitle: null,
              },
            ]
          : []),
        ...types
          .map((type) => {
            return {
              label: labels[type] || startCase(last(type.split('/'))),
              value: type,
              forward: !isTerminal(type),
              prefixIcon: PERSONA_OBJECT_ICONS[type],
              withSeparator: type === Categories.Persona,
              subtitle: labels[type] ? type : null, // display the actual value as subtitle if label is not directly derived from it
            };
          })
          .sort((a, b) => {
            if (a.value === Categories.Persona) {
              return -1;
            }
            if (b.value === Categories.Persona) {
              return 1;
            }
            return a.label.localeCompare(b.label);
          }),
      ];
    },
    [isTerminal],
  );

  const currentOptions = useMemo(() => {
    if (currentCategory === Categories.Templates && isWorkflowFieldObjectKey(selectedValue)) {
      const templates = templateOptionsByObjectKey[selectedValue] || [];
      return typeToOptions(
        [ALL_TEMPLATES_OPTION, ...templates.map((t) => t.value)],
        true,
        templates.reduce((acc, { label, value }) => ({ ...acc, [value]: label }), {}),
      );
    }

    return typeToOptions(
      categoryToOptions[currentCategory],
      currentCategory !== Categories.Initial,
      {},
    );
  }, [
    categoryToOptions,
    currentCategory,
    selectedValue,
    templateOptionsByObjectKey,
    typeToOptions,
  ]);

  const displayValue = useMemo(() => {
    const foundOption = currentOptions.find(
      (option) => option.value === selectedValue || option.value === selectedTemplate,
    );

    return foundOption && (isTerminal(foundOption.value) || !isOpen) ? foundOption.value : null;
  }, [currentOptions, isOpen, isTerminal, selectedTemplate, selectedValue]);

  const placeholderValue = useMemo(() => {
    // leverage placeholder text to provide context for the current nesting level
    if (currentCategory === Categories.Initial) {
      return 'Select type...';
    }
    if (currentCategory === Categories.Templates) {
      // hint that leaving the template field blank is equivalent to selecting all templates
      return startCase(ALL_TEMPLATES_OPTION);
    }
    return startCase(currentCategory);
  }, [currentCategory]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    // Avoid sending up ALL_TEMPLATES_OPTION to the caller
    const validTemplateValue = selectedTemplate === ALL_TEMPLATES_OPTION ? null : selectedTemplate;

    if (selectedValue !== value || validTemplateValue !== template) {
      onChange(
        selectedValue as JsonSchemaTypes | WorkflowFieldObjectKey,
        validTemplateValue ? validTemplateValue : undefined,
      );
    }
  }, [isOpen, onChange, selectedTemplate, selectedValue, template, value]);

  return (
    <Select
      value={displayValue}
      placeholder={placeholderValue}
      triggerProps={{
        'aria-label': label || 'Select type',
        onClick: () => {
          setIsOpen(true);
          setOutsideClickable(false);
        },
      }}
      open={isOpen}
      onValueChange={(newType: MenuItemType) => {
        if (newType === BACK_NAV_OPTION) {
          switch (currentCategory) {
            case Categories.Templates:
            case Categories.Report:
            case Categories.Verification:
              setCurrentCategory(Categories.Persona);
              break;
            default:
              setCurrentCategory(Categories.Initial);
          }
          return;
        }

        if (currentCategory === Categories.Templates) {
          setSelectedTemplate(newType === ALL_TEMPLATES_OPTION ? null : newType);
        } else {
          setSelectedValue(newType);
          const newValueHasSelectedTemplate =
            selectedTemplate && isWorkflowFieldObjectKey(newType)
              ? templateOptionsByObjectKey[newType]?.map((t) => t.value).includes(selectedTemplate)
              : false;
          if (!newValueHasSelectedTemplate) {
            setSelectedTemplate(null);
          }
        }

        if (isTerminal(newType)) {
          setIsOpen(false);
          setOutsideClickable(true);
        } else if (isWorkflowFieldObjectKey(newType) && templateOptionsByObjectKey[newType]) {
          setCurrentCategory(Categories.Templates);
        } else {
          setCurrentCategory(newType as Categories);
        }
      }}
    >
      {currentOptions.map((option) => (
        <SectionedOption
          key={option.value}
          value={option.value}
          withSeparator={option.withSeparator}
          prefixIcon={option.prefixIcon}
          suffixIcon={option.forward ? <ChevronRightIcon /> : undefined}
          aria-label={option.label}
          subtitle={option.subtitle || undefined}
          stretchLabel
        >
          {option.label}
        </SectionedOption>
      ))}
    </Select>
  );
};
